import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  jingqueAdminApi,
  ChatTemplate,
  HarnessSessionItem,
  LLMConnector,
  ToolDefinition,
} from '@/api/jingqueAdminApi'
import { readSSE } from '@/utils/sse'
import { toast } from '@/utils/toast'
import {
  TimelineItem,
  ToolCallItem,
  EventItem,
  nextKey,
  normalizeTimeline,
  parseArgs,
  prettyJson,
} from '@/components/admin/harness/timeline'
import { QUESTION_TOOL_NAME, QuestionAnswersPayload } from '@/components/admin/harness/QuestionCard'

// doc 120:多 tab 持久化 key(payload 含 user_id 作用域,B3)
const TABS_KEY = 'jingque-admin:agent-panel-tabs'

/** tab 视图(瘦投影,只暴露给 UI 渲染) */
interface TabView {
  tabId: string
  sessionId: string
  title: string
  running: boolean
}

/** Provider 对外暴露值 */
export interface AgentHarnessValue {
  // 元数据
  templates: ChatTemplate[]
  connectors: LLMConnector[]
  tools: ToolDefinition[]
  loadingMeta: boolean
  // 配置
  templateId: string
  setTemplateId: (v: string) => void
  selectedToolNames: string[]
  setSelectedToolNames: (v: string[]) => void
  disableThinking: boolean
  setDisableThinking: (v: boolean) => void
  connectorId: string
  setConnectorId: (v: string) => void
  tpl: ChatTemplate | undefined
  tplConnector: LLMConnector | undefined
  // doc 121:身份拆分 — adminUserId 恒定(登录用户);runAsUserId 控制工具查询视角
  // 会话归属全部用 adminUserId;run-as 不影响会话列表/tabs
  adminUserId: string
  runAsUserId: string
  setRunAsUserId: (v: string) => void
  sessions: HarnessSessionItem[]
  sessionsLoading: boolean
  renamingId: string | null
  setRenamingId: (v: string | null) => void
  renamingValue: string
  setRenamingValue: (v: string) => void
  // doc 120:tab 列表 + 激活 + 任意 tab 在跑
  tabs: TabView[]
  activeTabId: string
  anyRunning: boolean
  // 激活 tab 投影(原单例字段全部由 active tab 派生,Panel 零改动)
  contextId: string
  items: TimelineItem[]
  running: boolean
  liveKeyRef: React.MutableRefObject<string>
  hasPendingTool: boolean
  // doc 119:maxrounds 询问卡 pending 中(UI 标记)
  awaitingContinue: boolean
  totalTokens: number
  contextTokens: number
  cumMissTokens: number
  cumCacheTokens: number
  cumOutputTokens: number
  legacyNoDetail: number
  visibleSessions: HarnessSessionItem[]
  currentTitle: string
  selectedTools: Array<{ type: string; function: { name: string; description: string; parameters: any } }>
  // 动作
  send: (userText: string) => Promise<void>
  runFrontendTool: (item: ToolCallItem) => Promise<void>
  submitQuestion: (item: ToolCallItem, payload: QuestionAnswersPayload) => Promise<void>
  continueAfterMaxRounds: () => Promise<void>
  stop: () => void
  newSession: () => Promise<void>
  openSession: (s: HarnessSessionItem) => void
  submitRename: (sessionId: string) => Promise<void>
  removeSession: (sessionId: string) => Promise<void>
  loadSessions: (silent?: boolean) => Promise<void>
  // doc 120:tab 操作
  activateTab: (tabId: string) => void
  closeTab: (tabId: string) => void
  openTab: (sessionId?: string) => void
  // doc 123-Fix:上下文压缩
  compactionStatus: { est_tokens: number; context_tokens: number; ratio: number; trigger_ratio: number; enabled: boolean; basis: 'real' | 'estimate' } | null
  refreshCompactionStatus: () => Promise<void>
  runCompaction: () => Promise<void>
  liveCompaction: { content: string; trigger: string } | null
}

const Ctx = createContext<AgentHarnessValue | null>(null)

type TabMetrics = { total: number; context: number; miss: number; cache: number; output: number; legacy: number }
const EMPTY_METRICS = (): TabMetrics => ({ total: 0, context: 0, miss: 0, cache: 0, output: 0, legacy: 0 })

type TabState = {
  tabId: string
  sessionId: string          // '' = 未绑定
  items: TimelineItem[]
  liveKey: string
  running: boolean
  awaitingContinue: boolean
  loaded: boolean
  metrics: TabMetrics
}

type Archive = { v: 1; user_id: string; tabs: string[]; active: string }

function makeTabId() {
  return 't' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4)
}

function makeTab(liveKeyMap: Map<string, string>, opts?: { sessionId?: string; loaded?: boolean }): TabState {
  const tabId = makeTabId()
  const liveKey = nextKey('a')
  liveKeyMap.set(tabId, liveKey)
  return {
    tabId,
    sessionId: opts?.sessionId ?? '',
    items: [],
    liveKey,
    running: false,
    awaitingContinue: false,
    loaded: opts?.loaded ?? true,
    metrics: EMPTY_METRICS(),
  }
}

/** 从历史消息计算指标(原 loadHistory 内的累加逻辑,剥离为纯函数便于复用) */
function computeMetricsFromMessages(messages: any[]): TabMetrics {
  const histTokens = (messages || []).reduce(
    (s: number, m: any) => s + (Number(m.total_tokens) || 0),
    0,
  )
  const msgs = messages || []
  const lastAssistant = [...msgs].reverse().find((m: any) => m.role === 'assistant')
  const contextTokens = Number(lastAssistant?.metadata?.prompt_tokens) || 0
  let miss = 0, cache = 0, out = 0, legacy = 0
  for (const m of msgs) {
    if (m.role !== 'assistant') continue
    const meta = m.metadata
    if (meta && typeof meta.prompt_tokens === 'number') {
      const p = meta.prompt_tokens, c = Number(meta.cache_tokens) || 0
      miss += Math.max(p - c, 0)
      cache += c
      out += Number(meta.completion_tokens) || 0
    } else if (Number(m.total_tokens) > 0) {
      legacy++
    }
  }
  return { total: histTokens, context: contextTokens, miss, cache, output: out, legacy }
}

/** B5:loadHistory 错误信息是否表示 not-found → 触发自动关 tab */
function isNotFoundError(msg: string): boolean {
  return /not\s*found|不存在|404/i.test(msg)
}

/** B3:存档读取 + user_id 作用域校验 */
function readArchive(currentUserId: string): Archive | null {
  if (!currentUserId) return null
  try {
    const raw = localStorage.getItem(TABS_KEY)
    if (!raw) return null
    const p = JSON.parse(raw)
    if (!p || p.v !== 1) return null
    if (p.user_id !== currentUserId) return null
    if (!Array.isArray(p.tabs)) return null
    const tabs = (p.tabs as any[]).filter((s: any) => typeof s === 'string' && s.length > 0)
    const active = typeof p.active === 'string' ? p.active : ''
    return { v: 1, user_id: currentUserId, tabs, active }
  } catch {
    return null
  }
}

export const AgentHarnessProvider: FC<{ children: ReactNode }> = ({ children }) => {
  // ── 配置(全局共享) ──
  const [templates, setTemplates] = useState<ChatTemplate[]>([])
  const [connectors, setConnectors] = useState<LLMConnector[]>([])
  const [tools, setTools] = useState<ToolDefinition[]>([])
  const [loadingMeta, setLoadingMeta] = useState(false)
  const [templateId, setTemplateId] = useState<string>('')
  const [selectedToolNames, setSelectedToolNames] = useState<string[]>([])
  const [disableThinking, setDisableThinking] = useState(false)
  const [connectorId, setConnectorId] = useState<string>('')

  const tpl = useMemo(() => templates.find(t => t.templateId === templateId), [templates, templateId])
  const tplConnector = useMemo(
    // 模板存的是 connectorId(ChatTemplatesPage 保存语义),匹配 c.connectorId 而非 c.id
    () => connectors.find(c => c.connectorId === tpl?.connectorId),
    [connectors, tpl?.connectorId],
  )

  // ── 用户身份 ──
  // adminUserId:登录 admin(恒定,会话/落库/LLM 配置都用它)
  const adminUserId = useMemo<string>(() => {
    try {
      const u = JSON.parse(localStorage.getItem('auth_user') || '{}')
      return u.user_id || ''
    } catch { return '' }
  }, [])
  // runAsUserId:工具查询视角(空=本人;切不影响会话列表/tabs/进行中流)
  const [runAsUserId, setRunAsUserId] = useState<string>('')

  // ── 会话列表(全局) ──
  const [sessions, setSessions] = useState<HarnessSessionItem[]>([])
  const [sessionsLoading, setSessionsLoading] = useState(false)
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renamingValue, setRenamingValue] = useState('')

  // ── doc 120:tabs 状态表 + 镜像 refs ──
  const liveKeyMapRef = useRef(new Map<string, string>())
  const abortMapRef = useRef(new Map<string, AbortController>())
  // B1:createSession 串行化 — 避免两 tab 极短间隔拿到同一个空会话
  const createInFlightRef = useRef<Promise<any>>(Promise.resolve())

  // 初始化 tabs(B11:lazy pure read);doc 121:作用域恒 adminUserId
  const initialTabState = useMemo<{ tabs: TabState[]; active: string }>(() => {
    const arch = readArchive(adminUserId)
    if (arch) {
      const tabs = arch.tabs.map(sid => makeTab(liveKeyMapRef.current, { sessionId: sid, loaded: false }))
      const found = arch.active ? tabs.find(t => t.sessionId === arch.active) : null
      const active = (found ?? tabs[0])?.tabId ?? ''
      return { tabs, active }
    }
    const t = makeTab(liveKeyMapRef.current)
    return { tabs: [t], active: t.tabId }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [tabs, setTabs] = useState<TabState[]>(initialTabState.tabs)
  const [activeTabId, setActiveTabIdState] = useState<string>(initialTabState.active)

  // tabsRef 镜像:SSE 回调 / 跨 tab 查找(B1 重置判定)读最新状态,避免 setTabs updater 中再读旧值
  // doc 修复 #122:挂载即用 initialTabState 初始化,修「刷新后不点 tab 直接 send 找不到 tab」的 Bug A
  const tabsRef = useRef<TabState[]>(initialTabState.tabs)
  // activeTabIdRef 镜像:B10 SSE error case 判 active 用;挂载即初始化,后续由 effect + setActiveTabId 双重兜底
  const activeTabIdRef = useRef<string>(initialTabState.active)

  // doc 123-Fix:压缩状态徽标(节流 30s) + live 流式卡
  const [compactionStatus, setCompactionStatus] = useState<AgentHarnessValue['compactionStatus']>(null)
  const [liveCompaction, setLiveCompaction] = useState<AgentHarnessValue['liveCompaction']>(null)
  const compactionPollRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // 激活态镜像 + liveKey shim 初始化
  useEffect(() => {
    tabsRef.current = tabs
  }, [tabs])

  useEffect(() => {
    activeTabIdRef.current = activeTabId
  }, [activeTabId])

  useEffect(() => {
    const act = tabs.find(t => t.tabId === activeTabId)
    if (act) liveKeyRef.current = act.liveKey
  }, [activeTabId, tabs])

  // doc 修复 #122:commitTabs 同步提交器 — ref 与 state 同帧写,根除「await 后/同 tick 读 ref 拿旧值」(Bug B/C)
  const commitTabs = (next: TabState[] | ((prev: TabState[]) => TabState[])) => {
    const arr = typeof next === 'function' ? next(tabsRef.current) : next
    tabsRef.current = arr
    setTabs(arr)
  }

  const liveKeyRef = useRef<string>(liveKeyMapRef.current.get(activeTabId) ?? nextKey('a'))

  // 封装 setActive(同步镜像 ref)
  const setActiveTabId = (id: string) => {
    activeTabIdRef.current = id
    setActiveTabIdState(id)
    const act = tabsRef.current.find(t => t.tabId === id)
    if (act) liveKeyRef.current = act.liveKey
  }

  // 持久化签名(B4):只依赖 sid 列表 + active sid,流式期间 items/metrics 变化不触发写盘
  const tabsSig = useMemo(() => {
    const sids = tabs.filter(t => t.sessionId).map(t => t.sessionId).join(',')
    const activeSid = tabs.find(t => t.tabId === activeTabId)?.sessionId ?? ''
    return sids + '#' + activeSid
  }, [tabs, activeTabId])

  useEffect(() => {
    // doc 121:存档作用域恒 adminUserId;runAsUserId 变化不触发写盘
    if (!adminUserId) return
    try {
      localStorage.setItem(
        TABS_KEY,
        JSON.stringify({
          v: 1,
          user_id: adminUserId,
          tabs: tabs.filter(t => t.sessionId).map(t => t.sessionId),
          active: tabs.find(t => t.tabId === activeTabId)?.sessionId ?? '',
        }),
      )
    } catch {
      /* quota / disabled — silent */
    }
  }, [tabsSig, adminUserId])

  // ── 元数据加载 ──
  const loadMeta = async () => {
    setLoadingMeta(true)
    try {
      const [tpls, conns, tls, cfg] = await Promise.all([
        (async () => {
          const all: ChatTemplate[] = []
          let page = 1
          while (true) {
            const res = await jingqueAdminApi.chatTemplates.list(page, 100)
            all.push(...(res.items || []))
            if (page >= res.totalPages) break
            page++
          }
          return all
        })(),
        jingqueAdminApi.connectors.list(),
        jingqueAdminApi.toolDefinitions.list(),
        // 默认模板偏好:system_config.agent.harness.default_template_id(可后台热改),404/失败容错
        jingqueAdminApi.configs.get('agent.harness.default_template_id').catch(() => null),
      ])
      setTemplates(tpls)
      setConnectors(conns.connectors || [])
      setTools(tls.tools || [])
      if (tpls.length > 0 && !templateId) {
        // 默认模板偏好链:system_config.agent.harness.default_template_id → 兜底 xiaoqing-admin → 列表第一个
        const cfgValue = (cfg as any)?.value || ''
        const preferred =
          tpls.find(t => t.templateId === (cfgValue || 'xiaoqing-admin')) || tpls[0]
        setTemplateId(preferred.templateId)
        setSelectedToolNames(preferred.tools || [])
      }
    } catch (e: any) {
      toast.error('加载元数据失败: ' + (e.message || String(e)))
    } finally {
      setLoadingMeta(false)
    }
  }
  useEffect(() => { loadMeta() }, [])
  useEffect(() => {
    if (!tpl) return
    setSelectedToolNames(tpl.tools || [])
  }, [tpl?.templateId])

  // ── doc 120:loadHistory for one tab;doc 121:历史归属 admin ──
  const loadHistoryForTab = async (tabId: string) => {
    const t = tabsRef.current.find(x => x.tabId === tabId)
    if (!t || !t.sessionId) return
    try {
      const data = await jingqueAdminApi.agentHarness.loadSession(t.sessionId, adminUserId)
      const messages = data.messages || []
      const newLiveKey = nextKey('a')
      liveKeyMapRef.current.set(tabId, newLiveKey)
      commitTabs(prev => prev.map(x =>
        x.tabId === tabId
          ? { ...x, loaded: true, items: normalizeTimeline(messages), liveKey: newLiveKey, metrics: computeMetricsFromMessages(messages) }
          : x,
      ))
      if (activeTabIdRef.current === tabId) liveKeyRef.current = newLiveKey
    } catch (e: any) {
      const msg = String(e?.message || e)
      // B5:仅 not-found 自动关 tab;其它错误保留 + loaded=true 防循环
      if (isNotFoundError(msg)) {
        toast.error('会话已不存在,自动关闭 tab')
        closeTab(tabId)
      } else {
        commitTabs(prev => prev.map(x => x.tabId === tabId ? { ...x, loaded: true, items: [] } : x))
        toast.error('历史加载失败: ' + msg)
      }
    }
  }

  // 挂载后立即拉恢复激活 tab 的历史
  useEffect(() => {
    const active = tabsRef.current.find(t => t.tabId === activeTabId)
    if (active && active.sessionId && !active.loaded) {
      loadHistoryForTab(active.tabId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── 选中工具的 schemas(发给 LLM) ──
  const selectedTools = useMemo(
    () => tools
      .filter(t => selectedToolNames.includes(t.toolName))
      .map(t => ({
        type: 'function',
        function: {
          name: t.toolName,
          description: t.description,
          parameters: t.parameters || {},
        },
      })),
    [tools, selectedToolNames],
  )

  // ── tab-scoped timeline helpers(B1 重置 / B10 active 判读 也复用) ──
  const upsertLiveAssistant = (tabId: string, patch: { content?: string; reasoning?: string }) => {
    const key = liveKeyMapRef.current.get(tabId) ?? nextKey('a')
    commitTabs(prev => prev.map(t => {
      if (t.tabId !== tabId) return t
      const idx = t.items.findIndex(i => i.key === key)
      if (idx >= 0) {
        const next = [...t.items]
        next[idx] = { ...(next[idx] as any), ...patch }
        return { ...t, items: next }
      }
      return { ...t, items: [...t.items, { kind: 'assistant', key, content: patch.content ?? '', reasoning: patch.reasoning }] }
    }))
  }

  const finalizeLiveAssistant = (tabId: string) => {
    const newKey = nextKey('a')
    liveKeyMapRef.current.set(tabId, newKey)
    if (activeTabIdRef.current === tabId) liveKeyRef.current = newKey
    commitTabs(prev => prev.map(t => t.tabId === tabId ? { ...t, liveKey: newKey } : t))
  }

  const updateToolCall = (tabId: string, callId: string, patch: Partial<ToolCallItem>) => {
    commitTabs(prev => prev.map(t => {
      if (t.tabId !== tabId) return t
      return {
        ...t,
        items: t.items.map(i => i.kind === 'tool_call' && i.callId === callId ? { ...i, ...patch } : i),
      }
    }))
  }

  const addEvent = (tabId: string, eventType: EventItem['eventType'], title: string, detail?: string) => {
    commitTabs(prev => prev.map(t => {
      if (t.tabId !== tabId) return t
      return { ...t, items: [...t.items, { kind: 'event', key: nextKey('ev'), eventType, title, detail }] }
    }))
  }

  // doc 119:取消 tab 内 pending 的 question 卡(真实 + maxrounds 合成)
  const cancelPendingQuestions = (tabId: string) => {
    commitTabs(prev => prev.map(t => {
      if (t.tabId !== tabId) return t
      const hasPendingQ = t.items.some(
        i => i.kind === 'tool_call' && i.status === 'pending' && i.name === QUESTION_TOOL_NAME,
      )
      if (!hasPendingQ) return t
      const items = t.items.map(i => {
        if (i.kind === 'tool_call' && i.status === 'pending' && i.name === QUESTION_TOOL_NAME) {
          const isMaxrounds = i.callId.startsWith('maxrounds-')
          const note = isMaxrounds
            ? '未继续(已发起新消息)'
            : '用户未回答,直接发送了新消息'
          return {
            ...i,
            status: 'success',
            result: prettyJson({ answers: [], cancelled: true, note }),
            durationMs: Date.now() - (i.startedAt || Date.now()),
          } as ToolCallItem
        }
        return i
      })
      return { ...t, items, awaitingContinue: false }
    }))
  }

  // ── doc 120:tab 操作 ──
  const openTab = (sessionId: string = '') => {
    if (sessionId) {
      const existing = tabsRef.current.find(t => t.sessionId === sessionId)
      if (existing) {
        setActiveTabId(existing.tabId)
        return
      }
    }
    const t = makeTab(liveKeyMapRef.current, {
      sessionId,
      loaded: sessionId ? false : true,
    })
    commitTabs(prev => [...prev, t])
    setActiveTabId(t.tabId)
    if (sessionId) loadHistoryForTab(t.tabId)
  }

  const closeTab = (tabId: string) => {
    // abort 该 tab 流
    const c = abortMapRef.current.get(tabId)
    if (c) {
      c.abort()
      abortMapRef.current.delete(tabId)
    }
    liveKeyMapRef.current.delete(tabId)

    const wasActive = activeTabIdRef.current === tabId
    const current = tabsRef.current
    const idx = current.findIndex(t => t.tabId === tabId)
    if (idx === -1) return
    const newTabs = current.filter(t => t.tabId !== tabId)
    commitTabs(newTabs)

    if (!wasActive) return

    // B9:仅当关的是 active 才改变激活 → 优先右侧,其次左侧
    const next = newTabs[idx] ?? newTabs[idx - 1]
    if (next) {
      setActiveTabId(next.tabId)
    } else {
      // 全关 → 自动开一个未绑定新 tab
      const fresh = makeTab(liveKeyMapRef.current)
      commitTabs([fresh])
      setActiveTabId(fresh.tabId)
    }
  }

  const activateTab = (tabId: string) => {
    const tab = tabsRef.current.find(t => t.tabId === tabId)
    if (!tab) return
    setActiveTabId(tabId)
    // 懒加载:仅未拉过历史的 bound tab
    if (tab.sessionId && !tab.loaded) {
      loadHistoryForTab(tabId)
    }
  }

  // ── doc 120 + 修复 #122 + doc 121:bindSession 归属 admin ──
  const bindSession = async (tabId: string): Promise<string> => {
    if (!adminUserId) return ''
    const chain = createInFlightRef.current.then(
      () => jingqueAdminApi.agentHarness.createSession(adminUserId),
      () => jingqueAdminApi.agentHarness.createSession(adminUserId),
    )
    createInFlightRef.current = chain

    let result: { session_id: string; is_new: boolean; message_count: number }
    try {
      result = await chain
    } catch (e: any) {
      const local = 'harness_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36)
      commitTabs(prev => prev.map(t => t.tabId === tabId ? { ...t, sessionId: local, loaded: true } : t))
      toast.error('会话创建失败,已降级本地会话: ' + (e.message || String(e)))
      return local // 降级本地 uuid 仍 return,保持「降级后可继续发」旧行为
    }

    const sid = result.session_id

    // B1 撞车重置:复用了空会话 → 找其它 tab 绑定同 sid 且自身空 + 未在跑 → 重置
    if (!result.is_new && result.message_count === 0) {
      const other = tabsRef.current.find(
        t => t.tabId !== tabId && t.sessionId === sid && t.items.length === 0 && !t.running,
      )
      if (other) {
        const freshKey = nextKey('a')
        liveKeyMapRef.current.set(other.tabId, freshKey)
        if (activeTabIdRef.current === other.tabId) liveKeyRef.current = freshKey
        commitTabs(prev => prev.map(t => {
          if (t.tabId === tabId) return { ...t, sessionId: sid, loaded: true }
          if (t.tabId === other.tabId) {
            return {
              ...t,
              sessionId: '',
              items: [],
              liveKey: freshKey,
              metrics: EMPTY_METRICS(),
              awaitingContinue: false,
              loaded: true,
            }
          }
          return t
        }))
        return sid
      }
      // 理论竞态:对方 tab 已有 items 但同 sid(用户消息在 turn 开始落库之前)
      // createInFlightRef 串行化 + 用户消息落库已经把窗口压到亚秒,这里兜底
      toast.error('会话冲突:可能与另一 tab 共享,继续执行')
    }

    commitTabs(prev => prev.map(t => t.tabId === tabId ? { ...t, sessionId: sid, loaded: true } : t))
    return sid
  }

  // ── core:runTurn(tabId, sid, …) ──
  const runTurn = async (
    tabId: string,
    sid: string,
    userText: string,
    toolResult: any,
    isFirstTurn: boolean,
    options: { continueAfterMaxRounds?: boolean; compact?: boolean } = {},
  ) => {
    commitTabs(prev => prev.map(t => t.tabId === tabId ? { ...t, running: true } : t))
    const controller = new AbortController()
    abortMapRef.current.set(tabId, controller)

    let assistantContent = ''
    let assistantReasoning = ''
    let compactionLiveContent = '' // doc 123-Fix:auto 压缩累计摘要,finished 时 append 时间线 item

    try {
      const stream = await jingqueAdminApi.agentHarness.run({
        context_id: sid,
        // doc 121:会话归属不再放 payload;run_as 控制工具查询视角(空=本人)
        run_as: runAsUserId || undefined,
        user_text: userText,
        template_id: templateId,
        connector_id: connectorId || undefined,
        tools: selectedTools,
        tool_result: toolResult,
        continue_after_max_rounds: options.continueAfterMaxRounds || undefined,
        // doc 123-Fix:手动压缩复用 turn SSE(body 标志位同 continue_after_max_rounds 先例)
        compact: options.compact || undefined,
      })

      await readSSE(
        stream,
        ev => {
          switch (ev.type) {
            case 'chunk': {
              assistantContent += (ev.content as string) || ''
              upsertLiveAssistant(tabId, { content: assistantContent })
              break
            }
            case 'reasoning': {
              assistantReasoning += (ev.content as string) || ''
              upsertLiveAssistant(tabId, { content: assistantContent, reasoning: assistantReasoning })
              break
            }
            case 'tool_call': {
              if (assistantContent !== '' || assistantReasoning !== '') {
                finalizeLiveAssistant(tabId)
                assistantContent = ''
                assistantReasoning = ''
              }
              const calls = (ev.tool_calls as any[]) || []
              const location = (ev.location as 'agent' | 'frontend') || 'frontend'
              for (const tc of calls) {
                const callId = tc.id || nextKey('tc')
                commitTabs(prev => prev.map(t => {
                  if (t.tabId !== tabId) return t
                  return {
                    ...t,
                    items: [...t.items, {
                      kind: 'tool_call',
                      key: nextKey('call'),
                      callId,
                      name: tc.function?.name || 'unknown',
                      args: parseArgs(tc.function?.arguments),
                      location,
                      status: location === 'agent' ? 'running' : 'pending',
                      startedAt: Date.now(),
                    } as ToolCallItem],
                  }
                }))
              }
              break
            }
            case 'tool_result': {
              const callId = (ev.call_id as string) || ''
              updateToolCall(tabId, callId, {
                status: 'success',
                result: (ev.result as string) ?? '',
              })
              commitTabs(prev => prev.map(t => {
                if (t.tabId !== tabId) return t
                return {
                  ...t,
                  items: t.items.map(i =>
                    i.kind === 'tool_call' && i.callId === callId && i.startedAt
                      ? { ...i, durationMs: Date.now() - i.startedAt }
                      : i,
                  ),
                }
              }))
              break
            }
            case 'usage': {
              const u = (ev.usage as any) || {}
              const tt = u.total_tokens || 0
              commitTabs(prev => prev.map(t => {
                if (t.tabId !== tabId) return t
                const pt = Number(u.prompt_tokens) || 0
                const ct = Number(u.cache_tokens) || 0
                const ct2 = Number(u.completion_tokens) || 0
                return {
                  ...t,
                  metrics: {
                    ...t.metrics,
                    total: t.metrics.total + tt,
                    context: pt || t.metrics.context,
                    miss: t.metrics.miss + Math.max(pt - ct, 0),
                    cache: t.metrics.cache + ct,
                    output: t.metrics.output + ct2,
                  },
                }
              }))
              break
            }
            case 'topic_changed':
              addEvent(tabId, 'topic_changed', '话题切换', `${ev.prev_topic_name || '-'} → ${ev.topic_name || ''}`)
              break
            case 'flow_ended':
              addEvent(tabId, 'flow_ended', '流程结束', (ev.message as string) || '')
              break
            case 'max_rounds_reached': {
              const rounds = (ev as any).rounds ?? 20
              const synthId = `maxrounds-${Math.random().toString(36).slice(2, 10)}`
              commitTabs(prev => prev.map(t => {
                if (t.tabId !== tabId) return t
                return {
                  ...t,
                  awaitingContinue: true,
                  items: [...t.items, {
                    kind: 'tool_call',
                    key: nextKey('call'),
                    callId: synthId,
                    name: QUESTION_TOOL_NAME,
                    args: {
                      questions: [{
                        header: '工具轮上限',
                        question: `已达工具轮上限(${rounds} 轮),是否继续?`,
                        options: [
                          { label: '继续执行', description: '再获新一轮预算,从断点续跑' },
                          { label: '就此结束', description: '保留现有结果' },
                        ],
                      }],
                    },
                    location: 'frontend',
                    status: 'pending',
                    startedAt: Date.now(),
                  } as ToolCallItem],
                }
              }))
              break
            }
            case 'round_end': {
              finalizeLiveAssistant(tabId)
              if (ev.reason === 'frontend_tool' && activeTabIdRef.current === tabId) {
                toast.info('等待执行 frontend 工具(点击卡片上的「执行」)')
              }
              break
            }
            case 'error': {
              const msg = String(ev.message || '未知错误')
              addEvent(tabId, 'error', '错误', msg)
              // B10:仅 active tab 的错误弹 toast,后台 tab 仅 addEvent(切回可见)
              if (activeTabIdRef.current === tabId) toast.error(msg)
              break
            }
            case 'compaction_started': {
              // doc 123-Fix:流式卡置位 + toast
              const trigger = (ev as any).trigger ?? 'auto'
              if (activeTabIdRef.current === tabId) {
                setLiveCompaction({ content: '', trigger })
                toast.info('正在整理历史上下文…', 2000)
              }
              break
            }
            case 'compaction_chunk': {
              // doc 123-Fix:摘要逐 chunk 累计 + 流式展示
              const chunk = (ev as any).content ?? ''
              compactionLiveContent += chunk
              if (activeTabIdRef.current === tabId) {
                setLiveCompaction(prev => prev ? { ...prev, content: prev.content + chunk } : null)
              }
              break
            }
            case 'compaction_finished': {
              // doc 123-Fix:成功终态 — 手动 vs 自动 区分闭包 options.compact
              const headDropped = (ev as any).head_dropped ?? 0
              if (activeTabIdRef.current === tabId) {
                setLiveCompaction(null)
                toast.success(`上下文已整理(丢弃 ${headDropped} 条历史)`, 2000)
              }
              if (options.compact) {
                // 手动路径:reload 会话把折叠条入列(无其他在跑的流,可安全重载)
                // readSSE 回调为同步;异步 reload 用 IIFE 同步触发
                void (async () => {
                  try {
                    const data = await jingqueAdminApi.agentHarness.loadSession(sid, adminUserId)
                    const messages = data.messages || []
                    commitTabs(prev => prev.map(t => {
                      if (t.tabId !== tabId) return t
                      return {
                        ...t,
                        items: normalizeTimeline(messages),
                        liveKey: nextKey('a'),
                        metrics: computeMetricsFromMessages(messages),
                      }
                    }))
                  } catch (e: any) {
                    toast.error('重载会话失败: ' + (e.message || String(e)))
                  }
                })()
                refreshCompactionStatus()
              } else {
                // 自动路径:不能 reload(会打断紧随的 assistant 流式);追加 CompactionItem
                commitTabs(prev => prev.map(t => {
                  if (t.tabId !== tabId) return t
                  return {
                    ...t,
                    items: [...t.items, {
                      kind: 'compaction',
                      key: nextKey('compaction'),
                      headDropped,
                      summary: compactionLiveContent,
                      trigger: 'auto',
                      estTokensBefore: (ev as any).est_tokens_before,
                      estTokensAfter: (ev as any).est_tokens_after,
                    } as any],
                  }
                }))
                void refreshCompactionStatus()
              }
              break
            }
            case 'compaction_result': {
              // doc 123-Fix:统一非成功终态
              const status = (ev as any).status ?? 'unknown'
              const reason = (ev as any).reason ?? ''
              // doc 123-Fix4:纵深防御 —— skipped 是 auto 路径正常心跳,即使后端回归也不打扰用户
              if (status === 'skipped') break
              if (activeTabIdRef.current === tabId) {
                setLiveCompaction(null)
                const msg = (() => {
                  switch (status) {
                    case 'disabled': return '压缩已禁用'
                    case 'too_short': return '历史过短,无需压缩'
                    case 'head_empty': return '无可压缩内容'
                    case 'dangling_tool': return '有未完成的前端工具调用,完成后重试'
                    case 'cancelled': return '已取消'
                    case 'failed': return `压缩失败: ${reason}`
                    default: return `压缩: ${status}`
                  }
                })()
                if (status === 'failed') toast.error(msg)
                else if (status === 'dangling_tool' || status === 'cancelled') toast.warning(msg)
                else toast.info(msg, 2000)
              }
              break
            }
          }
        },
        controller.signal,
      )
    } catch (e: any) {
      if (e?.name !== 'AbortError') {
        const msg = e.message || String(e)
        addEvent(tabId, 'error', '错误', msg)
        if (activeTabIdRef.current === tabId) toast.error(msg)
      }
    } finally {
      commitTabs(prev => prev.map(t => t.tabId === tabId ? { ...t, running: false } : t))
      abortMapRef.current.delete(tabId)
      // doc 123-Fix:异常/中止兜底清流式卡,避免残留
      if (activeTabIdRef.current === tabId) setLiveCompaction(null)
      if (isFirstTurn) maybeAutoNameAfterTurn(sid)
    }
  }

  // ── send / runFrontendTool / submitQuestion / continueAfterMaxRounds / stop ──
  const send = async (userText: string) => {
    if (!userText.trim() || !tpl) return
    const tabId = activeTabIdRef.current
    const tab = tabsRef.current.find(t => t.tabId === tabId)
    if (!tab) return
    if (tab.running) return
    if (tab.items.some(
      i => i.kind === 'tool_call' && i.status === 'pending' && i.name !== QUESTION_TOOL_NAME,
    )) {
      toast.error('请先执行待处理的 frontend 工具')
      return
    }

    // doc 119:发新消息前自动取消 pending 的 question 卡
    cancelPendingQuestions(tabId)

    let sid = tab.sessionId
    if (!sid) {
      // doc 修复 #122:bindSession 直接返回 sid,不再回读 tabsRef(commitTabs 已保证同帧一致)
      sid = await bindSession(tabId)
      if (!sid) return
    }

    // 首轮识别(send 前该 tab items 为空 → 触发自动命名)
    const wasFirstTurn = tab.items.length === 0
    commitTabs(prev => prev.map(t => t.tabId === tabId
      ? { ...t, items: [...t.items, { kind: 'user', key: nextKey('u'), content: userText }] }
      : t,
    ))
    await runTurn(tabId, sid, userText, null, wasFirstTurn)
  }

  const runFrontendTool = async (item: ToolCallItem) => {
    const tabId = activeTabIdRef.current
    const tab = tabsRef.current.find(t => t.tabId === tabId)
    if (!tab || !tab.sessionId) return
    updateToolCall(tabId, item.callId, { status: 'running' })
    const result = JSON.stringify({ echoed: item.args }, null, 2)
    const durationMs = Date.now() - item.startedAt
    updateToolCall(tabId, item.callId, { status: 'success', result, durationMs })
    await runTurn(tabId, tab.sessionId, '', { call_id: item.callId, result }, false)
  }

  const submitQuestion = async (item: ToolCallItem, payload: QuestionAnswersPayload) => {
    const tabId = activeTabIdRef.current
    const tab = tabsRef.current.find(t => t.tabId === tabId)
    if (!tab || !tab.sessionId) return
    updateToolCall(tabId, item.callId, {
      status: 'success',
      result: prettyJson(payload),
      durationMs: Date.now() - item.startedAt,
    })
    if (item.callId.startsWith('maxrounds-')) {
      const answer = payload.answers?.[0]
      const continueChosen = answer?.selected?.includes('继续执行')
      if (continueChosen) {
        commitTabs(prev => prev.map(t => t.tabId === tabId ? { ...t, awaitingContinue: false } : t))
        await runTurn(tabId, tab.sessionId, '', null, false, { continueAfterMaxRounds: true })
      }
      return
    }
    await runTurn(tabId, tab.sessionId, '', { call_id: item.callId, result: payload }, false)
  }

  const continueAfterMaxRounds = async () => {
    const tabId = activeTabIdRef.current
    const tab = tabsRef.current.find(t => t.tabId === tabId)
    if (!tab || tab.running || !tab.sessionId) return
    commitTabs(prev => prev.map(t => t.tabId === tabId ? { ...t, awaitingContinue: false } : t))
    await runTurn(tabId, tab.sessionId, '', null, false, { continueAfterMaxRounds: true })
  }

  const stop = () => {
    const tabId = activeTabIdRef.current
    const c = abortMapRef.current.get(tabId)
    if (c) c.abort()
  }

  // ── 会话管理 ──
  const newSession = async () => {
    openTab('') // 懒绑定,不再预建 DB 会话
    await loadSessions(true)
  }

  const loadSessions = async (silent = false) => {
    // doc 121:会话列表恒 admin — 切 run-as 不重拉/不清空
    if (!adminUserId) {
      setSessions([])
      return
    }
    if (!silent) setSessionsLoading(true)
    try {
      const r = await jingqueAdminApi.agentHarness.listSessions(adminUserId, 1, 50)
      setSessions(r.data || [])
    } catch (e: any) {
      toast.error('加载会话列表失败: ' + (e.message || String(e)))
    } finally {
      if (!silent) setSessionsLoading(false)
    }
  }

  // doc 121:挂载时拉一次会话列表;run-as 变化不重拉、不中断流、不清 tabs
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadSessions() }, [adminUserId])

  const openSession = (s: HarnessSessionItem) => {
    const existing = tabsRef.current.find(t => t.sessionId === s.session_id)
    if (existing) {
      // B8:已开 tab 再点侧栏 → 激活 + 静默重拉历史(行为对等当前 openSession)
      setActiveTabId(existing.tabId)
      loadHistoryForTab(existing.tabId)
      return
    }
    const t = makeTab(liveKeyMapRef.current, { sessionId: s.session_id, loaded: false })
    commitTabs(prev => [...prev, t])
    setActiveTabId(t.tabId)
    loadHistoryForTab(t.tabId)
  }

  const submitRename = async (sessionId: string) => {
    const name = renamingValue.trim()
    if (!name) {
      setRenamingId(null)
      return
    }
    try {
      await jingqueAdminApi.agentHarness.renameSession(sessionId, name, adminUserId)
      toast.success('已重命名')
      setRenamingId(null)
      await loadSessions(true)
    } catch (e: any) {
      toast.error('重命名失败: ' + (e.message || String(e)))
    }
  }

  const removeSession = async (sessionId: string) => {
    try {
      await jingqueAdminApi.agentHarness.deleteSession(sessionId, adminUserId)
      toast.success('已删除')
      // 连带关 tab(B9:closeTab 内部判 active,非 active 不改变激活)
      const t = tabsRef.current.find(x => x.sessionId === sessionId)
      if (t) closeTab(t.tabId)
      await loadSessions(true)
    } catch (e: any) {
      toast.error('删除失败: ' + (e.message || String(e)))
    }
  }

  // ── doc 123:上下文压缩 ──

  const refreshCompactionStatus = async () => {
    const cid = activeTabIdRef.current
      ? tabsRef.current.find(t => t.tabId === activeTabIdRef.current)?.sessionId
      : ''
    if (!cid) {
      setCompactionStatus(null)
      return
    }
    try {
      const s = await jingqueAdminApi.agentHarness.compactionStatus(cid)
      setCompactionStatus(s)
    } catch {
      setCompactionStatus(null)
    }
  }

  const runCompaction = async () => {
    // doc 123-Fix:手动压缩复用 runTurn(compact:true),SSE + running 状态全共用
    const activeTab = tabsRef.current.find(t => t.tabId === activeTabIdRef.current)
    const cid = activeTab?.sessionId
    if (!cid) {
      toast.warning('请先选择会话')
      return
    }
    if (activeTab.running) {
      toast.warning('正在处理,请稍候')
      return
    }
    if (activeTab.items.some(
      i => i.kind === 'tool_call' && i.status === 'pending' && i.name !== QUESTION_TOOL_NAME,
    )) {
      toast.error('有未完成的前端工具,执行后再压缩')
      return
    }
    // 无 user_text/tool_result;finished 分支按 manual reload
    await runTurn(activeTabIdRef.current, cid, '', null, false, { compact: true })
  }

  // 徽标:active tab 切换时拉一次 + 节流 30s
  useEffect(() => {
    refreshCompactionStatus()
    if (compactionPollRef.current) clearTimeout(compactionPollRef.current)
    compactionPollRef.current = setTimeout(() => {
      refreshCompactionStatus()
    }, 30000)
    return () => {
      if (compactionPollRef.current) clearTimeout(compactionPollRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabId])

  // 首轮对话自动 AI 命名(静默)— sid 由 runTurn 捕获传入
  const maybeAutoNameAfterTurn = async (sid: string) => {
    if (!templateId || !adminUserId) return
    try {
      await jingqueAdminApi.agentHarness.autoNameSession(sid, templateId, adminUserId)
    } catch {
      /* 静默 */
    } finally {
      await loadSessions(true)
    }
  }

  // ── 激活 tab 投影(原单例字段全部由 active tab 派生,Panel 零改动) ──
  const activeTab = tabs.find(t => t.tabId === activeTabId) ?? tabs[0]
  const items: TimelineItem[] = activeTab?.items ?? []
  const running = activeTab?.running ?? false
  const contextId = activeTab?.sessionId ?? ''
  const awaitingContinue = activeTab?.awaitingContinue ?? false
  const hasPendingTool = items.some(
    i => i.kind === 'tool_call' && i.status === 'pending' && i.name !== QUESTION_TOOL_NAME,
  )
  const totalTokens = activeTab?.metrics.total ?? 0
  const contextTokens = activeTab?.metrics.context ?? 0
  const cumMissTokens = activeTab?.metrics.miss ?? 0
  const cumCacheTokens = activeTab?.metrics.cache ?? 0
  const cumOutputTokens = activeTab?.metrics.output ?? 0
  const legacyNoDetail = activeTab?.metrics.legacy ?? 0
  const anyRunning = tabs.some(t => t.running)

  // 流式结束自动刷新徽标
  useEffect(() => {
    if (anyRunning) return
    refreshCompactionStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyRunning])

  const visibleSessions = useMemo(
    () => sessions.filter(s => s.message_count > 0),
    [sessions],
  )
  const currentTitle = useMemo(() => {
    const cur = sessions.find(s => s.session_id === contextId)
    if (cur?.title) return cur.title
    if (!contextId) return '新会话'
    return `新会话 (${contextId.slice(0, 8)}…)`
  }, [sessions, contextId])

  const tabsView: TabView[] = useMemo(() => tabs.map(t => {
    const s = sessions.find(x => x.session_id === t.sessionId)
    const title = s?.title
      || (t.sessionId ? `新会话 (${t.sessionId.slice(0, 8)}…)` : '新会话')
    return { tabId: t.tabId, sessionId: t.sessionId, title, running: t.running }
  }), [tabs, sessions])

  const value: AgentHarnessValue = {
    templates, connectors, tools, loadingMeta,
    templateId, setTemplateId,
    selectedToolNames, setSelectedToolNames,
    disableThinking, setDisableThinking,
    connectorId, setConnectorId,
    tpl, tplConnector,
    adminUserId, runAsUserId, setRunAsUserId,
    sessions, sessionsLoading,
    renamingId, setRenamingId, renamingValue, setRenamingValue,
    tabs: tabsView,
    activeTabId,
    anyRunning,
    contextId,
    items, running, liveKeyRef, hasPendingTool, awaitingContinue,
    totalTokens, contextTokens, cumMissTokens, cumCacheTokens, cumOutputTokens, legacyNoDetail,
    visibleSessions, currentTitle, selectedTools,
    send, runFrontendTool, submitQuestion, continueAfterMaxRounds, stop,
    newSession, openSession, submitRename, removeSession, loadSessions,
    activateTab, closeTab, openTab,
    compactionStatus, refreshCompactionStatus, runCompaction, liveCompaction,
  }

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAgentHarness(): AgentHarnessValue {
  const v = useContext(Ctx)
  if (!v) throw new Error('useAgentHarness must be used within AgentHarnessProvider')
  return v
}